import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ImageUpload } from "./ImageUpload";

const LABELS: Record<string, string> = {
  hero_title: "Hero Title",
  hero_subtitle: "Hero Subtitle",
  hero_cta: "Hero Button Text",
  hero_badge_1: "Hero Badge 1",
  hero_badge_2: "Hero Badge 2",
  hero_badge_3: "Hero Badge 3",
  hero_image_url: "Hero Image",
  brand_tagline: "Brand Tagline",
  shop_name: "Shop Name",
  shop_address: "Shop Address",
  shop_phone: "Phone Number",
  shop_email: "Email",
  shop_hours: "Opening Hours",
  whatsapp_number: "WhatsApp Number",
  logo_url: "Logo Image",
  why_title: "Why Us — Heading",
  why_1_title: "Feature 1 Title",
  why_1_desc: "Feature 1 Description",
  why_2_title: "Feature 2 Title",
  why_2_desc: "Feature 2 Description",
  why_3_title: "Feature 3 Title",
  why_3_desc: "Feature 3 Description",
  book_eyebrow: "Booking — Small Label",
  book_title: "Booking — Heading",
  book_subtitle: "Booking — Subheading",
  book_perks_title: "Booking — Perks Title",
  book_perk_1: "Booking — Perk 1",
  book_perk_2: "Booking — Perk 2",
  book_perk_3: "Booking — Perk 3",
  footer_about: "Footer — About Text",
  gallery_title: "Gallery — Heading",
  gallery_subtitle: "Gallery — Subheading",
  testimonials_title: "Testimonials — Heading",
  testimonials_subtitle: "Testimonials — Subheading",
};

// Section order with the keys belonging to each section
const SECTIONS: { id: string; title: string; keys: string[] }[] = [
  { id: "brand", title: "🏷️ Brand & Logo", keys: ["shop_name", "brand_tagline", "logo_url"] },
  { id: "hero", title: "🎯 Hero (Top Banner)", keys: ["hero_title", "hero_subtitle", "hero_cta", "hero_badge_1", "hero_badge_2", "hero_badge_3", "hero_image_url"] },
  { id: "why", title: "⭐ Why Choose Us", keys: ["why_title", "why_1_title", "why_1_desc", "why_2_title", "why_2_desc", "why_3_title", "why_3_desc"] },
  { id: "gallery", title: "🖼️ Gallery Section Headings", keys: ["gallery_title", "gallery_subtitle"] },
  { id: "testimonials", title: "💬 Testimonials Headings", keys: ["testimonials_title", "testimonials_subtitle"] },
  { id: "book", title: "📅 Booking Section", keys: ["book_eyebrow", "book_title", "book_subtitle", "book_perks_title", "book_perk_1", "book_perk_2", "book_perk_3"] },
  { id: "contact", title: "📞 Contact Info (Phone, Email, Address)", keys: ["shop_phone", "whatsapp_number", "shop_email", "shop_address", "shop_hours"] },
  { id: "footer", title: "📄 Footer", keys: ["footer_about"] },
];

interface Row { id: string; key: string; value: string; }

export function ContentTab() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  async function load() {
    const { data } = await supabase.from("site_content").select("*").order("key");
    setRows(data || []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function save(id: string, key: string, value: string) {
    setSaving(id);
    const { error } = await supabase.from("site_content").update({ value }).eq("id", id);
    setSaving(null);
    if (error) toast.error(error.message); else toast.success(`${LABELS[key] || key} saved`);
  }

  if (loading) return <p className="text-muted-foreground">Loading...</p>;

  // Filter out nav_links (managed in Navigation tab) — sectioned keys
  const sectionedKeys = new Set(SECTIONS.flatMap((s) => s.keys));
  const others = rows.filter((r) => !sectionedKeys.has(r.key) && r.key !== "nav_links");

  function renderField(r: Row) {
    const isImage = r.key.endsWith("_url");
    const isLong = r.value.length > 60 || r.key.includes("subtitle") || r.key.includes("desc") || r.key.includes("about");
    return (
      <div key={r.id} className="rounded-lg border border-border bg-card p-3">
        <Label className="text-xs uppercase tracking-wider text-muted-foreground">{LABELS[r.key] || r.key}</Label>
        {isImage ? (
          <div className="mt-2">
            <ImageUpload
              value={r.value}
              onChange={(url) => {
                setRows((rs) => rs.map((x) => x.id === r.id ? { ...x, value: url } : x));
                save(r.id, r.key, url);
              }}
              folder={r.key}
              label=""
            />
          </div>
        ) : isLong ? (
          <>
            <Textarea rows={3} value={r.value}
              onChange={(e) => setRows((rs) => rs.map((x) => x.id === r.id ? { ...x, value: e.target.value } : x))}
              className="mt-1.5" />
            <Button size="sm" onClick={() => save(r.id, r.key, r.value)} disabled={saving === r.id} className="mt-2">
              {saving === r.id ? "Saving..." : "Save"}
            </Button>
          </>
        ) : (
          <>
            <Input value={r.value}
              onChange={(e) => setRows((rs) => rs.map((x) => x.id === r.id ? { ...x, value: e.target.value } : x))}
              className="mt-1.5" />
            <Button size="sm" onClick={() => save(r.id, r.key, r.value)} disabled={saving === r.id} className="mt-2">
              {saving === r.id ? "Saving..." : "Save"}
            </Button>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl">Website Content</h2>
        <p className="text-sm text-muted-foreground">Click any section below to edit. Changes appear instantly.</p>
      </div>

      <Accordion type="multiple" className="space-y-2">
        {SECTIONS.map((section) => {
          const items = section.keys
            .map((k) => rows.find((r) => r.key === k))
            .filter((r): r is Row => !!r);
          if (items.length === 0) return null;
          return (
            <AccordionItem key={section.id} value={section.id} className="rounded-xl border border-border bg-background px-4">
              <AccordionTrigger className="text-base font-semibold hover:no-underline">{section.title}</AccordionTrigger>
              <AccordionContent>
                <div className="grid gap-3 md:grid-cols-2 pb-2">
                  {items.map(renderField)}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
        {others.length > 0 && (
          <AccordionItem value="other" className="rounded-xl border border-border bg-background px-4">
            <AccordionTrigger className="text-base font-semibold hover:no-underline">⚙️ Other</AccordionTrigger>
            <AccordionContent>
              <div className="grid gap-3 md:grid-cols-2 pb-2">{others.map(renderField)}</div>
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>
    </div>
  );
}
